import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import "./App.css";

// 🔹 Backend Base URL
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8090";

// 🔹 Simulated logged-in user
const userId = "u1";

function App() {
  const [alerts, setAlerts] = useState([]);
  const [destinationName, setDestinationName] = useState("");
  const [alertDistance, setAlertDistance] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCoords, setSelectedCoords] = useState(null);

  const [currentLocation, setCurrentLocation] = useState(null);
  const [currentAddress, setCurrentAddress] = useState("");
  const [roadData, setRoadData] = useState({});
  const [triggeredAlerts, setTriggeredAlerts] = useState({});

  const watchIdRef = useRef(null);
  const alertsRef = useRef([]);
  const lastAddressFetchRef = useRef(0);
  const alertAudioRef = useRef(null);
  const triggeredRef = useRef({});

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    try {
      alertAudioRef.current = new Audio("/alert.mp3");
    } catch {
      console.warn("Audio not supported");
    }
  }, []);

  const showNotification = (text) => {
    if (Notification.permission === "granted") {
      new Notification("Destination Alert 🚆", { body: text });
    }

    if (alertAudioRef.current) {
      alertAudioRef.current.currentTime = 0;
      alertAudioRef.current.play().catch(() => {});
    }
  };

  // ✅ Reverse geocoding via backend
  const fetchAddress = useCallback(async (lat, lon) => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/alerts/reverse?lat=${lat}&lon=${lon}`,
      );
      setCurrentAddress(res.data);
    } catch (err) {
      console.error("Reverse geocoding error:", err);
    }
  }, []);

  const searchLocation = async (text) => {
    setDestinationName(text);

    if (text.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await axios.get(
        `https://nominatim.openstreetmap.org/search?q=${text}&format=json&limit=5`,
      );
      setSuggestions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const selectSuggestion = (place) => {
    setDestinationName(place.display_name);
    setSelectedCoords({ lat: place.lat, lon: place.lon });
    setSuggestions([]);
  };

  // ✅ Road distance + notifications
  const fetchRoadDistance = useCallback(
    async (alertId, userLat, userLon, destLat, destLon, alertDistanceKm) => {
      try {
        const res = await axios.get(
          `https://router.project-osrm.org/route/v1/driving/${userLon},${userLat};${destLon},${destLat}?overview=false`,
        );

        const meters = res.data.routes[0].distance;
        const seconds = res.data.routes[0].duration;
        const km = meters / 1000;
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        setRoadData((prev) => ({
          ...prev,
          [alertId]: {
            km: km.toFixed(2),
            time: `${hours}h ${minutes}m`,
          },
        }));

        if (!triggeredRef.current[alertId]) {
          triggeredRef.current[alertId] = {
            mainAlert: false,
            fiveKmAlert: false,
          };
        }

        const alertState = triggeredRef.current[alertId];

        if (km <= 5 && !alertState.fiveKmAlert) {
          showNotification("⚠️ Only 5 km left to destination!");
          alertState.fiveKmAlert = true;
        }

        if (km <= alertDistanceKm && !alertState.mainAlert) {
          showNotification(`🔔 Within ${alertDistanceKm} km of destination`);
          alertState.mainAlert = true;
        }

        setTriggeredAlerts({ ...triggeredRef.current });
      } catch (err) {
        console.error("Road distance error:", err);
      }
    },
    [],
  );

  // ✅ Start GPS tracking
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) return;
    if (watchIdRef.current !== null) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords;
        setCurrentLocation({ latitude, longitude });

        const now = Date.now();
        if (now - lastAddressFetchRef.current > 20000) {
          fetchAddress(latitude, longitude);
          lastAddressFetchRef.current = now;
        }

        alertsRef.current.forEach((alert) => {
          fetchRoadDistance(
            alert.id,
            latitude,
            longitude,
            alert.latitude,
            alert.longitude,
            alert.alertDistance,
          );
        });
      },
      (err) => console.error(err),
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      },
    );
  }, [fetchAddress, fetchRoadDistance]);

  // ✅ Fetch alerts for logged-in user
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/alerts?userId=${userId}`);
      setAlerts(res.data.reverse());
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    Notification.requestPermission();
    fetchAlerts();
    startTracking();

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js");
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [fetchAlerts, startTracking]);

  // ✅ Create Alert
  const createAlert = async () => {
    if (!selectedCoords) return alert("Select destination from suggestions");

    try {
      await axios.post(`${API_BASE}/api/alerts`, {
        userId,
        destinationName,
        latitude: parseFloat(selectedCoords.lat),
        longitude: parseFloat(selectedCoords.lon),
        alertDistance: parseFloat(alertDistance),
        active: true,
      });

      fetchAlerts();
      setDestinationName("");
      setAlertDistance("");
      setSelectedCoords(null);
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ DELETE ALERT
  const deleteAlert = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/alerts/${id}?userId=${userId}`);
      fetchAlerts();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  return (
    <div className="app">
      <h1 className="title">Destination Alert</h1>

      {currentLocation && (
        <div className="card">
          <h3>Your Live Location</h3>
          <p>Lat: {currentLocation.latitude}</p>
          <p>Lon: {currentLocation.longitude}</p>
          <h4>📍 {currentAddress || "Fetching address..."}</h4>
        </div>
      )}

      <div className="card">
        <h2>Create Alert</h2>

        <input
          className="input inpt"
          type="text"
          placeholder="Search city, street..."
          value={destinationName}
          onChange={(e) => searchLocation(e.target.value)}
        />

        {suggestions.map((s, i) => (
          <div
            key={i}
            onClick={() => selectSuggestion(s)}
            className="suggestion"
          >
            {s.display_name}
          </div>
        ))}

        <input
          className="input inpt"
          type="number"
          placeholder="Alert Distance (KM)"
          value={alertDistance}
          onChange={(e) => setAlertDistance(e.target.value)}
        />

        <button className="button" onClick={createAlert}>
          Create Alert
        </button>
      </div>

      <h2>Saved Alerts</h2>

      {alerts.map((alert) => {
        const road = roadData[alert.id];

        return (
          <div key={alert.id} className="card">
            <strong>{alert.destinationName}</strong>
            <p>Alert Distance: {alert.alertDistance} KM</p>

            {road && (
              <>
                <p>🛣️ Road Distance: {road.km} km</p>
                <p>⏱️ Travel Time: {road.time}</p>
              </>
            )}

            <p>
              Status:{" "}
              {triggeredAlerts[alert.id]?.mainAlert
                ? "🔴 Triggered"
                : "🟢 Active"}
            </p>

            {/* ✅ Delete Button */}
            <button
              className="button btm"
              style={{ background: "#ff4d4f", marginTop: "10px" }}
              onClick={() => deleteAlert(alert.id)}
            >
              Delete Alert
            </button>
          </div>
        );
      })}

      <footer style={{ textAlign: "center", padding: "25px", color: "#666" }}>
        <div>
          <a
            href="https://www.linkedin.com/in/tuntun-kumar-webdeveloper"
            target="_blank"
            rel="noreferrer"
          >
            LinkedIn
          </a>{" "}
          |{" "}
          <a
            href="https://github.com/aksingh6m1"
            target="_blank"
            rel="noreferrer"
          >
            GitHub
          </a>{" "}
          |{" "}
          <a
            href="https://www.instagram.com/diceyalok/"
            target="_blank"
            rel="noreferrer"
          >
            Instagram
          </a>
        </div>
        <p>Copyright ©Feb-2026 All Rights Reserved @diceyalok</p>
      </footer>
    </div>
  );
}

export default App;
